
/**
 * Um domínio: o conjunto de valores que um atributo do tipo seleção aceita.
 *
 * Vive no ambiente, e não no atributo, para ser reusado por atributos de coleções
 * diferentes — a mesma relação que o fluxo de trabalho tem com as coleções.
 *
 * A ORDEM das opções é a do array, como nos estados do fluxo: não há campo de posição,
 * e reordenar é reescrever a lista. É essa ordem que a listagem usa para ordenar a
 * coluna, em vez do id ou do nome.
 */
type Domain = {
    alias: string
    name: string
    options: DomainOption[]
}

export type DomainOption = {
    /**
     * Interno, nunca exposto ao usuário, e atribuído pelo SERVIDOR.
     *
     * É o que o valor da instância guarda, e o que faz esse valor sobreviver a renomear
     * e reordenar. Opção nova vai ao servidor sem id justamente para que o contador
     * fique de um lado só: id reaproveitado faria o dado antigo apontar, em silêncio,
     * para a opção nova que ocupou o lugar.
     */
    id: number
    name: string
    /**
     * O que as fórmulas citam: `prioridade == "alta"`. Sai do nome ao criar e não muda depois de
     * gravado — renomear muda só o nome, e a fórmula continua valendo. O valor do item continua
     * guardando o `id`.
     */
    alias: string
    /** Nome-base de cor do Vuetify (`teal`, `indigo`); os tons saem do `shade`. */
    color: string
}

/** A opção ainda sem id, que é como ela vai ao servidor quando é nova. */
export type DomainOptionDraft = Omit<DomainOption, 'id'> & { id?: number }

/**
 * O domínio em branco, que é por onde criar começa.
 *
 * **Sem opção nenhuma.** Houve aqui uma segunda semente que já trazia uma opção vazia, porque o
 * editor de então só acrescentava por um "+" no cabeçalho e um domínio novo nasceria sem nada a
 * que o usuário pudesse responder. O editor de hoje tem o campo de criar sempre à vista, no
 * lugar em que a próxima opção vai aparecer — a opção em branco virou um campo com uma bolinha
 * e nenhum nome, que não é opção nenhuma.
 *
 * Que domínio sem opção não se grava continua valendo, e agora é dito por quem pode resolver:
 * o editor exige o mínimo e acusa no campo de criar.
 */
export function emptyDomain(): Domain {
    return {alias: '', name: '', options: []}
}

/**
 * Os métodos de leitura que as telas precisam, sem tocar no objeto guardado.
 *
 * Segue o `enhanceWorkflow`: função e não classe, reconstruída a cada uso, porque o
 * objeto do workspace é substituído nas atualizações e um getter que fechasse sobre o
 * recebido leria dados velhos para sempre.
 */
export function enhanceDomain(domain: Domain) {
    return {
        ...domain,
        optionWith(id: number | undefined) {
            return domain.options.find(option => option.id === id)
        },
        /** A posição no domínio, que é a ordem que a listagem usa. Ausente vai para o fim. */
        positionOf(id: number | undefined) {
            const index = domain.options.findIndex(option => option.id === id)
            return index < 0 ? Number.MAX_SAFE_INTEGER : index
        }
    }
}

export type EnhancedDomain = ReturnType<typeof enhanceDomain>

/**
 * O domínio de um atributo de seleção, achado numa lista de domínios.
 *
 * É O ponto de resolução do app inteiro: quem exibe ou edita um valor de seleção recebe
 * a lista (`workspace.domains`, ou o `_domains` da coleção onde não há workspace) e
 * pergunta aqui. O atributo guarda só o alias, como o relacionamento guarda só o
 * alias da coleção de destino.
 */
export function domainOfProperty(property: { domain?: string }, domains: Domain[] | undefined) {
    const found = (domains || []).find(domain => domain.alias === property.domain)
    return found && enhanceDomain(found)
}

export default Domain
